import { ActionIcon, Button, Code, Group, Popover, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconHelpCircle } from '@tabler/icons-react';

let notificationCount = 0;

/**
 * The ? next to a setting's label: what it does, 2–4 examples and one watch-out, in a popover over
 * the form so the form never moves. `onUse(value)` applies an example; when it returns a function,
 * the notification it shows offers Undo. `triggerLabel` swaps the ? for a small button (Examples ▾).
 */
const HelpPopover = ({ label, what, examples = [], onUse, watchOut, triggerLabel }) => {
  const use = (example) => {
    const undo = onUse(example.value);
    notificationCount += 1;
    const id = `help-use-${notificationCount}`;
    notifications.show({
      id,
      message: (
        <Group justify="space-between" gap="xs" wrap="nowrap">
          <Text size="sm">
            {label}: used “{example.label}”
          </Text>
          {typeof undo === 'function' && (
            <Button
              size="compact-xs"
              variant="subtle"
              onClick={() => {
                undo();
                notifications.hide(id);
              }}
            >
              Undo
            </Button>
          )}
        </Group>
      ),
    });
  };

  return (
    <Popover width="max-content" position="bottom-start" withArrow shadow="md">
      <Popover.Target>
        {triggerLabel ? (
          <Button variant="default" size="xs" aria-label={`${triggerLabel}: ${label}`}>
            {triggerLabel}
          </Button>
        ) : (
          <ActionIcon variant="subtle" color="gray" c="dimmed" size="xs" radius="xl" aria-label={`Help: ${label}`}>
            <IconHelpCircle size={16} stroke={1.5} />
          </ActionIcon>
        )}
      </Popover.Target>
      {/* Grows with a long example up to ~560px, never wider than the screen; code wraps instead of scrolling. */}
      <Popover.Dropdown miw={320} style={{ maxWidth: 'min(560px, calc(100vw - 32px))' }}>
        <Text size="xs">{what}</Text>
        {examples.length > 0 && (
          <Stack gap={4} mt="xs">
            {examples.map((example) => (
              <Group key={example.label} justify="space-between" gap="xs" wrap="nowrap">
                <Code fz="xs" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {example.label}
                </Code>
                {onUse && (
                  <Button
                    size="compact-xs"
                    variant="light"
                    flex="none"
                    aria-label={`Use ${example.label}`}
                    onClick={() => use(example)}
                  >
                    Use
                  </Button>
                )}
              </Group>
            ))}
          </Stack>
        )}
        {watchOut && (
          <Text size="xs" c="dimmed" mt="xs">
            Watch out: {watchOut}
          </Text>
        )}
      </Popover.Dropdown>
    </Popover>
  );
};

export default HelpPopover;
